import React, { useContext } from "react";
import { useMutation } from "@apollo/client";
import { v4 } from "uuid";
import { withTranslation } from "react-i18next";
import { withRouter } from "react-router";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";

import { Icon, Button, Table, Text } from "tabler-react";

import AppSettingsContext from "../../context/AppSettingsContext";
import { get_list_query_variables } from "./tools";
import FinanceInvoicesStatus from "../../ui/FinanceInvoiceStatus";
import {
  GET_INVOICES_QUERY,
  DELETE_FINANCE_INVOICE,
  SEND_INVOICE_REMINDER,
  SEND_INVOICE_EMAIL,
} from "./queries";
import { TOKEN_REFRESH } from "../../../queries/system/auth";
import { refreshTokenAndOpenExportLinkInNewTab } from "../../../tools/refresh_token_and_open_export_link";
import confirm_delete from "../../../tools/confirm_delete";
import confirm_reminder from "../../../tools/confirm_reminder";
import moment from "moment";

function FinanceInvoicesList({
  t,
  history,
  match,
  invoices,
  showBtnPDF = false,
  showColRelation = false,
}) {
  const appSettings = useContext(AppSettingsContext);
  const dateFormat = appSettings.dateFormat;

  const [doTokenRefresh] = useMutation(TOKEN_REFRESH);
  const [deleteFinanceInvoice] = useMutation(DELETE_FINANCE_INVOICE);
  const [sendInvoiceReminder, { loading: sendingReminder }] = useMutation(
    SEND_INVOICE_REMINDER,
    {
      onCompleted: (data) => {
        if (data.sendInvoiceReminder.success) {
          toast.success(
            t("finance.invoices.reminder_send_success", {
              email: data.sendInvoiceReminder.emailSentTo,
            }),
            {
              position: toast.POSITION.BOTTOM_RIGHT,
            },
          );
        } else {
          toast.error(t("general.error_sad_smiley"), {
            position: toast.POSITION.BOTTOM_RIGHT,
          });
        }
      },
      onError: (error) => {
        console.error("Error sending reminder:", error);
        toast.error(t("general.error_sad_smiley"), {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
      },
    },
  );

  const [sendInvoiceEmail, { loading: sendingEmail }] = useMutation(
    SEND_INVOICE_EMAIL,
    {
      onCompleted: (data) => {
        if (data.sendInvoiceEmail.success) {
          toast.success(
            t("finance.invoices.email_send_success", {
              email: data.sendInvoiceEmail.emailSentTo,
            }),
            {
              position: toast.POSITION.BOTTOM_RIGHT,
            },
          );
        } else {
          toast.error(t("general.error_sad_smiley"), {
            position: toast.POSITION.BOTTOM_RIGHT,
          });
        }
      },
      onError: (error) => {
        console.error("Error sending email:", error);
        toast.error(t("general.error_sad_smiley"), {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
      },
    },
  );

  const handleReminderClick = (invoice) => {
    confirm_reminder({
      t,
      invoice,
      onConfirm: (invoice) => {
        sendInvoiceReminder({
          variables: {
            input: {
              id: invoice.id,
            },
          },
        });
      },
    });
  };

  const handleEmailClick = (invoice) => {
    const recipientName =
      invoice.business?.name || invoice.account?.fullName || "Unknown";
    const emailAddress = invoice.account?.email;

    if (!emailAddress) {
      toast.error(t("general.error_no_email"), {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
      return;
    }

    const confirmMessage = t("finance.invoices.send_email_confirm", {
      recipient: recipientName,
      email: emailAddress,
    });

    if (window.confirm(confirmMessage)) {
      sendInvoiceEmail({
        variables: {
          input: {
            id: invoice.id,
          },
        },
      });
    }
  };

  const renderReminderButton = (invoice) => {
    if (invoice.status !== "OVERDUE") {
      return null;
    }

    return (
      <Button
        color="warning"
        icon="mail"
        className="mr-2"
        size="sm"
        onClick={() => handleReminderClick(invoice)}
        disabled={sendingReminder}
      >
        {sendingReminder
          ? t("general.sending")
          : t("finance.invoices.send_reminder")}
      </Button>
    );
  };

  const renderEmailButton = (invoice) => {
    if (!invoice.account?.email) {
      return null;
    }

    return (
      <Button
        color="primary"
        icon="send"
        className="mr-2"
        size="sm"
        onClick={() => handleEmailClick(invoice)}
        disabled={sendingEmail}
      >
        {sendingEmail ? t("general.sending") : t("finance.invoices.send_email")}
      </Button>
    );
  };

  return (
    <Table cards>
      <Table.Header>
        <Table.Row key={v4()}>
          <Table.ColHeader>{t("general.status")}</Table.ColHeader>
          <Table.ColHeader>
            {t("finance.invoices.invoice_number")} &{" "}
            {t("finance.invoices.summary")}
          </Table.ColHeader>
          {showColRelation && (
            <Table.ColHeader>{t("finance.invoices.relation")}</Table.ColHeader>
          )}
          <Table.ColHeader>
            {t("finance.invoices.date")} & {t("finance.invoices.due")}
          </Table.ColHeader>
          {/* <Table.ColHeader>{t('finance.invoices.due')}</Table.ColHeader> */}
          <Table.ColHeader>{t("general.total")}</Table.ColHeader>
          <Table.ColHeader>{t("general.balance")}</Table.ColHeader>
          <Table.ColHeader></Table.ColHeader>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {invoices.edges.map(({ node }) => (
          <Table.Row key={v4()}>
            <Table.Col key={v4()}>
              <FinanceInvoicesStatus status={node.status} /> <br />
              {node.business && !showColRelation && (
                <Link to={"/relations/b2b/" + node.business.id + "/edit"}>
                  <small>
                    <Icon name="home" /> {node.business.name}
                  </small>
                </Link>
              )}
            </Table.Col>
            <Table.Col key={v4()}>
              <Link to={"/finance/invoices/edit/" + node.id}>
                {node.invoiceNumber}
              </Link>
              <br />
              <Text.Small color="gray">{node.summary.trunc(28)}</Text.Small>
            </Table.Col>
            {showColRelation && (
              <Table.Col key={v4()}>
                {node.account && (
                  <Link
                    to={"/relations/accounts/" + node.account.id + "/profile"}
                  >
                    <Icon name="user" /> {node.account.fullName} <br />
                  </Link>
                )}
                {node.business && (
                  <Link to={"/relations/b2b/" + node.business.id + "/edit"}>
                    <small>
                      <Icon name="home" /> {node.business.name}
                    </small>
                  </Link>
                )}
              </Table.Col>
            )}
            <Table.Col key={v4()}>
              {moment(node.dateSent).format(dateFormat)} <br />
              {moment(node.dateDue).format(dateFormat)}
            </Table.Col>
            <Table.Col key={v4()}>{node.totalDisplay}</Table.Col>
            <Table.Col key={v4()}>{node.balanceDisplay}</Table.Col>
            <Table.Col className="text-right" key={v4()}>
              {renderReminderButton(node)}
              {renderEmailButton(node)}
              {showBtnPDF && (
                <Button
                  color="secondary"
                  icon="printer"
                  className="mr-2"
                  size="sm"
                  onClick={() =>
                    refreshTokenAndOpenExportLinkInNewTab(
                      t,
                      doTokenRefresh,
                      history,
                      `/d/export/invoice/pdf/${node.id}`,
                    )
                  }
                >
                  {t("general.pdf")}
                </Button>
              )}
              <Link to={"/finance/invoices/edit/" + node.id}>
                <Button className="btn-sm" color="secondary">
                  {t("general.edit")}
                </Button>
              </Link>
              <button
                className="icon btn btn-link btn-sm"
                title={t("general.delete")}
                href=""
                onClick={() => {
                  confirm_delete({
                    t: t,
                    msgConfirm: t("finance.invoices.delete_confirm_msg"),
                    msgDescription: <p>{node.invoiceNumber}</p>,
                    msgSuccess: t("finance.invoices.deleted"),
                    deleteFunction: deleteFinanceInvoice,
                    functionVariables: {
                      variables: {
                        input: {
                          id: node.id,
                        },
                      },
                      refetchQueries: [
                        {
                          query: GET_INVOICES_QUERY,
                          variables: get_list_query_variables(),
                        },
                      ],
                    },
                  });
                }}
              >
                <span className="text-red">
                  <Icon prefix="fe" name="trash-2" />
                </span>
              </button>
            </Table.Col>
          </Table.Row>
        ))}
      </Table.Body>
    </Table>
  );
}

export default withTranslation()(withRouter(FinanceInvoicesList));
